import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import * as projectService from '../services/projectService';

interface SidebarProps {
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
}

export function Sidebar({ isCollapsed, setIsCollapsed }: SidebarProps): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useAuth();

  const isAdmin = user?.role === 'company_admin' || user?.role === 'platform_admin';
  const isPlatformAdmin = user?.role === 'platform_admin';

  // Extract project ID from URL if we're on a project page
  const projectIdMatch = location.pathname.match(/\/projects\/([a-f0-9-]+)/i);
  const currentProjectId = projectIdMatch ? projectIdMatch[1] : null;

  // State for project list
  const [projects, setProjects] = useState<any[]>([]);
  const [isProjectsExpanded, setIsProjectsExpanded] = useState(() => {
    const saved = localStorage.getItem('sidebar_projects_expanded');
    return saved !== null ? saved === 'true' : true; // Default expanded
  });
  // State for current project (to get AI requirements count)
  const [currentProject, setCurrentProject] = useState<any>(null);

  // Load projects on mount
  useEffect(() => {
    async function loadProjects() {
      if (!token) return;
      try {
        const response = await projectService.listProjects({}, token);
        setProjects(response.projects || []);
      } catch (error) {
        console.error('Error loading projects:', error);
      }
    }
    loadProjects();
  }, [token]);

  // Load current project details when projectId changes
  useEffect(() => {
    async function loadCurrentProject() {
      if (!token || !currentProjectId) {
        setCurrentProject(null);
        return;
      }
      try {
        const response = await projectService.getProjectById(currentProjectId, token);
        setCurrentProject(response.project);
      } catch (error) {
        console.error('Error loading current project:', error);
      }
    }
    loadCurrentProject();
  }, [token, currentProjectId]);

  const menuItems = [
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      ),
      label: 'Dashboard',
      path: '/dashboard',
      show: true
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
        </svg>
      ),
      label: 'Projects',
      path: '/projects',
      show: true
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      label: 'Manage Users',
      path: '/users',
      show: isAdmin
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      label: 'AI Configuration',
      path: '/admin/ai-config',
      show: isPlatformAdmin
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      label: 'Company Settings',
      path: '/company/settings',
      show: isAdmin
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
      ),
      label: 'JIRA Integration',
      path: '/company/jira',
      show: isAdmin
    }
  ];

  // Project-specific menu items (shown when viewing a project)
  const projectMenuItems = currentProjectId ? [
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      ),
      label: 'Requirements',
      path: `/projects/${currentProjectId}/requirements`,
      show: true
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      label: 'AI Generated Requirements',
      path: `/projects/${currentProjectId}/ai-requirements`,
      show: true
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      label: 'Requirement Metrics',
      path: `/projects/${currentProjectId}/requirements/metrics`,
      show: true
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      label: 'Metrics',
      path: `/projects/${currentProjectId}/metrics`,
      show: true
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M11.571 11.513H0a5.218 5.218 0 0 0 5.232 5.215h2.13v2.057A5.215 5.215 0 0 0 12.575 24V12.518a1.005 1.005 0 0 0-1.005-1.005zm5.723-5.756H5.736a5.215 5.215 0 0 0 5.215 5.214h2.129v2.058a5.218 5.218 0 0 0 5.215 5.214V6.758a1.001 1.001 0 0 0-1.001-1.001zM23.013 0H11.455a5.215 5.215 0 0 0 5.215 5.215h2.129v2.057A5.215 5.215 0 0 0 24 2.057V1.001A1.001 1.001 0 0 0 23.013 0z"/>
        </svg>
      ),
      label: 'JIRA Info',
      path: `/projects/${currentProjectId}/jira`,
      show: true
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      label: 'Timeline',
      path: `/projects/${currentProjectId}/gantt`,
      show: true
    }
  ] : [];

  const visibleMenuItems = menuItems.filter(item => item.show);
  const visibleProjectMenuItems = projectMenuItems.filter(item => item.show);

  const isActive = (path: string) => location.pathname === path;

  return (
    <div
      className={`${
        isCollapsed ? 'w-20' : 'w-64'
      } bg-surface-primary border-r border-border-primary transition-all duration-300 ease-in-out flex flex-col`}
    >
      {/* Sidebar Header */}
      <div className="h-16 flex items-center justify-center px-4 border-b border-border-primary">
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="p-2 rounded-md hover:bg-background-secondary transition-colors"
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? (
            <svg className="w-5 h-5 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
            </svg>
          ) : (
            <svg className="w-5 h-5 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            </svg>
          )}
        </button>
      </div>

      {/* Navigation Items */}
      <nav className="flex-1 py-4 overflow-y-auto">
        {visibleMenuItems.map((item) => (
          <div key={item.path}>
            <button
              onClick={() => {
                if (item.label === 'Projects') {
                  const newState = !isProjectsExpanded;
                  setIsProjectsExpanded(newState);
                  localStorage.setItem('sidebar_projects_expanded', String(newState));
                } else {
                  navigate(item.path);
                }
              }}
              className={`w-full flex items-center ${
                isCollapsed ? 'justify-center px-4' : 'px-6'
              } py-3 transition-colors ${
                isActive(item.path)
                  ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 border-r-4 border-indigo-600 dark:border-indigo-400'
                  : 'text-text-primary hover:bg-background-secondary'
              }`}
              title={isCollapsed ? item.label : undefined}
            >
              <span className={isActive(item.path) ? 'text-indigo-600 dark:text-indigo-400' : 'text-text-secondary'}>
                {item.icon}
              </span>
              {!isCollapsed && (
                <>
                  <span className="ml-3 text-sm font-medium flex-1 text-left">{item.label}</span>
                  {item.label === 'Projects' && projects.length > 0 && (
                    <svg
                      className={`w-4 h-4 text-text-tertiary transition-transform duration-200 ${
                        isProjectsExpanded ? 'rotate-90' : ''
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                </>
              )}
            </button>

            {/* Nested Project List */}
            {item.label === 'Projects' && isProjectsExpanded && !isCollapsed && projects.length > 0 && (
              <div className="bg-background-secondary">
                {projects.map((project) => (
                  <button
                    key={project.id}
                    onClick={() => navigate(`/projects/${project.id}`)}
                    className={`w-full flex items-center px-10 py-2 text-sm transition-colors ${
                      location.pathname === `/projects/${project.id}`
                        ? 'text-indigo-600 dark:text-indigo-400 font-medium bg-indigo-50 dark:bg-indigo-900/30'
                        : 'text-text-secondary hover:bg-background-tertiary hover:text-text-primary'
                    }`}
                    title={project.name}
                  >
                    <span className="truncate">{project.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* Project-specific navigation (shown when viewing a project) */}
        {visibleProjectMenuItems.length > 0 && (
          <>
            <div className="my-3 px-6">
              <div className="border-t border-border-primary"></div>
            </div>
            {!isCollapsed && (
              <div className="px-6 py-2">
                <span className="text-xs font-semibold text-text-tertiary uppercase tracking-wider">
                  Project
                </span>
              </div>
            )}
            {visibleProjectMenuItems.map((item) => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center ${
                  isCollapsed ? 'justify-center px-4' : 'px-6'
                } py-2.5 transition-colors ${
                  isActive(item.path)
                    ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 border-r-4 border-indigo-600 dark:border-indigo-400'
                    : 'text-text-primary hover:bg-background-secondary'
                }`}
                title={isCollapsed ? item.label : undefined}
              >
                <span className={isActive(item.path) ? 'text-indigo-600 dark:text-indigo-400' : 'text-text-secondary'}>
                  {item.icon}
                </span>
                {!isCollapsed && (
                  <>
                    <span className="ml-3 text-sm font-medium">{item.label}</span>
                    {item.label === 'AI Generated Requirements' && currentProject && (
                      <span className="ml-auto px-2 py-0.5 text-xs font-medium text-text-secondary bg-background-tertiary rounded-full whitespace-nowrap">
                        {currentProject.ai_generated_requirements_count || 0}/200
                      </span>
                    )}
                  </>
                )}
              </button>
            ))}
          </>
        )}
      </nav>
    </div>
  );
}

export default Sidebar;
